/**
 * PulseWidget - mostra l'ULTIMO evento del sistema in tempo reale con animazione pulsante.
 * Es: "INFOTRE ha accettato un preventivo · 47 secondi fa"
 *
 * Si aggancia automaticamente al NotificheContext (che fa polling ogni 15s) e mostra
 * la notifica piu recente con un'indicazione "tempo trascorso" che si aggiorna ogni 10s.
 */
import { useEffect, useState } from 'react';
import { Activity, Bell } from 'lucide-react';
import { useNotifiche } from '../contexts/NotificheContext';
import { Link } from 'react-router-dom';

const TIPO_ICONS = {
  preventivo_accettato: { emoji: '🟢', color: 'text-emerald-500', label: 'Accettazione preventivo' },
  scorta_minima: { emoji: '🟠', color: 'text-orange-500', label: 'Magazzino sotto scorta' },
  scadenza_verifica: { emoji: '🟡', color: 'text-amber-500', label: 'Verifica in scadenza' },
  scadenza_abbonamento: { emoji: '🔵', color: 'text-cyan-500', label: 'Abbonamento in scadenza' },
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)} secondi fa`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  return `${Math.floor(diff / 86400)} giorni fa`;
}

export default function PulseWidget() {
  const { notifiche } = useNotifiche();
  const [tick, setTick] = useState(0);

  // Re-render ogni 10s per aggiornare "X secondi fa"
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(i);
  }, []);

  const last = notifiche && notifiche.length > 0 ? notifiche[0] : null;
  const cfg = last ? TIPO_ICONS[last.tipo] || { emoji: '⚪', color: 'text-slate-500', label: 'Evento' } : null;

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden" data-testid="pulse-widget">
      <div className="px-4 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </div>
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Live · Ultimo evento</span>
        </div>
        <Activity className="h-3.5 w-3.5 text-emerald-500" />
      </div>
      <div className="p-4">
        {last ? (
          <Link to="/" className="block group" key={`${last.id}-${tick}`}>
            <div className="flex items-start gap-3">
              <div className={`text-2xl ${cfg.color}`}>{cfg.emoji}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground group-hover:text-emerald-600 transition-colors line-clamp-2">
                  {last.messaggio}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{cfg.label}</span>
                  <span className="text-[11px] text-muted-foreground">·</span>
                  <span className="text-[11px] font-mono text-emerald-600">{timeAgo(last.data)}</span>
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Bell className="h-5 w-5" />
            <p className="text-sm">Nessun evento recente. Le notifiche compaiono qui in tempo reale.</p>
          </div>
        )}
      </div>
    </div>
  );
}
